import { useEffect, useState, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  api,
  write,
  oidc,
  token,
  type RuntimeConfig,
  type User,
} from "./lib/api";
import Shell from "./components/Shell";
import Library from "./pages/Library";
import { Spinner, Button, ErrorBox, ToastProvider } from "./components/ui";
const Editor = lazy(() => import("./pages/Editor"));
const Surveys = lazy(() => import("./pages/Surveys"));
const Results = lazy(() => import("./pages/Results"));
const Settings = lazy(() => import("./pages/Settings"));
const Patient = lazy(() => import("./pages/Patient"));
function StaffApp() {
  const [config, setConfig] = useState<RuntimeConfig>();
  const [user, setUser] = useState<User>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  useEffect(() => {
    let alive = true;
    (async () => {
      const cfg = await api<RuntimeConfig>("/config");
      if (!alive) return;
      setConfig(cfg);
      if (location.pathname === "/auth/callback" && cfg.issuer) {
        const u = await oidc(cfg).signinRedirectCallback();
        sessionStorage.setItem("iron.staff-token", u.access_token);
        sessionStorage.removeItem("iron.require-fresh-login");
        history.replaceState(null, "", "/questionnaires");
      }
      if (!token() && cfg.demo) {
        const result = await write<{ accessToken: string }>("/auth/demo", {});
        if (!alive) return;
        sessionStorage.setItem("iron.staff-token", result.accessToken);
      }
      if (
        !token() &&
        cfg.issuer &&
        !sessionStorage.getItem("iron.require-fresh-login")
      ) {
        const u = await oidc(cfg).getUser();
        if (u && !u.expired)
          sessionStorage.setItem("iron.staff-token", u.access_token);
      }
      if (token()) {
        try {
          const me = await api<User>("/me");
          if (alive) setUser(me);
        } catch (e) {
          sessionStorage.removeItem("iron.staff-token");
          if (alive) setError((e as Error).message);
        }
      }
    })()
      .catch((e) => {
        if (alive) setError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);
  if (loading) return <Spinner />;
  if (!user || !config)
    return (
      <div className="login-page">
        <img src="/iron.svg" alt="Iron" />
        <h1>
          Good care starts
          <br />
          with good questions.
        </h1>
        <p>Sign in to your clinic’s questionnaire workspace.</p>
        {error && <ErrorBox message={error} />}
        <Button
          onClick={async () => {
            if (config?.issuer)
              await oidc(config).signinRedirect(
                sessionStorage.getItem("iron.require-fresh-login")
                  ? { prompt: "login", max_age: 0 }
                  : undefined,
              );
            else window.location.reload();
          }}
        >
          {config?.issuer ? "Sign in with your clinic" : "Reload workspace"}
        </Button>
      </div>
    );
  return (
    <Routes>
      <Route element={<Shell config={config} user={user} />}>
        <Route index element={<Navigate to="/questionnaires" replace />} />
        <Route path="questionnaires" element={<Library />} />
        <Route path="templates" element={<Library templatesOnly />} />
        <Route
          path="questionnaires/:id"
          element={<Editor key={location.pathname} />}
        />
        <Route path="assignments" element={<Surveys />} />
        <Route path="results" element={<Results />} />
        <Route path="settings" element={<Settings />} />
        <Route
          path="auth/callback"
          element={<Navigate to="/questionnaires" replace />}
        />
        <Route
          path="*"
          element={
            <div className="page">
              <h1>Page not found</h1>
              <a href="/questionnaires">Return to questionnaires</a>
            </div>
          }
        />
      </Route>
    </Routes>
  );
}
export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <Suspense fallback={<Spinner />}>
          <Routes>
            <Route path="/s/:id" element={<Patient />} />
            <Route path="*" element={<StaffApp />} />
          </Routes>
        </Suspense>
      </ToastProvider>
    </BrowserRouter>
  );
}
